import type {ComposerImage} from '@github-ui/agent-sessions-interface/components/ChatComposer'

export const IMAGE_MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.gif': 'image/gif',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
}

const ALLOWED_IMAGE_MIME_TYPES = new Set(Object.values(IMAGE_MIME_BY_EXTENSION))

export function isAllowedImageMimeType(type: string): boolean {
  return ALLOWED_IMAGE_MIME_TYPES.has(type)
}

function extensionFor(file: File): string {
  const extensionStart = file.name.lastIndexOf('.')
  if (extensionStart <= 0 || extensionStart === file.name.length - 1) return ''
  return file.name.slice(extensionStart).toLowerCase()
}

/**
 * The content type to send for an image, or `null` when the file isn't one we accept.
 *
 * A blank type falls back to the extension, because files arrive untyped often enough. A type that
 * is present but isn't an image does not: the browser positively identified those bytes as
 * something else, so trusting the name instead would attach a `data:image/png` URL wrapping a PDF.
 */
export function resolveImageMimeType(file: File): string | null {
  if (isAllowedImageMimeType(file.type)) return file.type
  if (file.type !== '') return null
  return IMAGE_MIME_BY_EXTENSION[extensionFor(file)] ?? null
}

/**
 * Reads an image `File`/`Blob` into a {@link ComposerImage}. Resolves `null`
 * when the file isn't an image or can't be read, so callers can simply filter.
 */
export function readImageFile(file: File, id: string, fallbackName: string): Promise<ComposerImage | null> {
  const contentType = resolveImageMimeType(file)
  if (!contentType) return Promise.resolve(null)
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const commaIndex = result.indexOf(',')
      const data = commaIndex >= 0 ? result.slice(commaIndex + 1) : ''
      if (!data) {
        resolve(null)
        return
      }
      resolve({
        id,
        name: file.name || fallbackName,
        dataUrl: `data:${contentType};base64,${data}`,
        contentType,
        data,
      })
    }
    reader.onerror = () => resolve(null)
    reader.readAsDataURL(file)
  })
}
